import { useRef, useEffect, useMemo } from "react"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import { selectActiveBattles, selectGame, selectViewerId } from "src/store/game"
import { relationRgb, relationOf } from "src/utils/teamColors"
import { activeWorld } from "src/utils/world"

import "./MiniMap.css"
import { GridIcon } from "src/components/icons"
import { usePointerContext } from "src/utils/input"


// The board, one pixel-ish square per cell, the retro way: sea, land, and then only what matters —
// cities and stacks of units in the colour of what they are to you, brighter the bigger they are.
// Country colours are the big map's business; here they would only be noise.
const SEA = "#153a5a"
const LAND = "#2d3f2b"

const unitsIn = (metadata) =>
  Object.values(metadata?.units || {}).reduce((total, unit) => total + (unit.number || 0), 0)

// 1 unit faint, ~500 fully lit: a log scale, so a garrison of 5 and one of 5 000 both register
// without the small one vanishing.
const intensity = (count) => Math.min(1, 0.35 + (Math.log10(Math.max(1, count)) / Math.log10(500)) * 0.65)

const rgba = ([r, g, b], a) => `rgba(${r}, ${g}, ${b}, ${a})`

const MiniMap = ({ setClickOnMinimap, radarPosition, gridVisible = true, onToggleGrid, speed = null }) => {
  const { t } = useTranslation()
  // The verb these hints use: a finger taps, a mouse clicks (see utils/input).
  const pointerContext = usePointerContext()
  const miniMapRef = useRef(null)
  const canvasRef = useRef(null)
  const radarMarkerRef = useRef(null)
  const game = useSelector(selectGame)
  const userId = useSelector(selectViewerId)
  // Where the fires are. From the selector, not from a presence check of this module's own: a
  // besieging attacker waits at the gates as a *movement*, so counting teams in `metadata_by_user`
  // misses exactly the sieges worth showing (owner, 2026-09-04).
  const battles = useSelector(selectActiveBattles)

  // What to light up, recomputed only when the game payload changes.
  const marks = useMemo(() => {
    const cells = game?.metadata?.cells || {}
    const users = game?.metadata?.users || {}
    const teamOf = (id) => users[String(id)]?.team
    // By relation, never by team (owner, 2026-09-10): mine green, an ally's blue, an enemy's red,
    // whichever side the viewer is on. Painted by team it read correctly for a human by accident
    // and inverted for a zombie, whose own cities came out red and whose enemies came out green.
    const paintOf = (id) => relationRgb(relationOf({ ownerId: id, userId, teamOf }))
    const out = []
    for (const [key, cell] of Object.entries(cells)) {
      const [col, row] = key.split("-").map(Number)
      // Troops: the strongest presence on the cell decides the colour, its size the brightness.
      let best = null
      for (const [holderId, metadata] of Object.entries(cell.metadata_by_user || {})) {
        const n = unitsIn(metadata)
        if (n > 0 && (!best || n > best.n)) best = { n, team: teamOf(holderId), paint: paintOf(holderId) }
      }
      if (cell.owner != null) out.push({ col, row, paint: paintOf(cell.owner), alpha: 1, city: true })
      else if (best?.team) out.push({ col, row, paint: best.paint, alpha: intensity(best.n) })
      if (cell.owner != null && best?.team && best.team !== teamOf(cell.owner)) {
        // Someone else's troops on a city: the city keeps its colour, the intruder shows as a dot.
        out.push({ col, row, paint: best.paint, alpha: intensity(best.n), dot: true })
      }
    }
    // The fires, marked apart so the draw loop can make them burn.
    for (const battle of battles) out.push({ col: battle.col, row: battle.row, battle: true })
    for (const movement of game?.metadata?.movements || []) {
      const position = movement.position || movement.path?.[movement.current_index ?? 0]
      if (!position) continue
      out.push({
        col: position[0],
        row: position[1],
        paint: paintOf(movement.user_id),
        alpha: intensity(unitsIn(movement)),
        dot: true,
      })
    }
    // No crown marks. A cell here is about 2px at the radar's widest, so the gold ring drawn on
    // one covered the square whole and every starting city — mine and the enemy's — read as one
    // gold dot, with the colour saying which was which painted underneath and buried. The radar
    // answers "whose is that": green, blue, red, and nothing else (owner, 2026-09-10). Which city
    // wears a crown is the roster's, the dock's and the tooltip's business, where there is room to
    // say it.
    return out
  }, [game, userId, battles])

  // Draw: sea and land from the world matrix, then the marks. Sized to the box it is in. Battles
  // pulse — a fire is an emergency, and a static dot does not read as one.
  useEffect(() => {
    const canvas = canvasRef.current
    const box = miniMapRef.current
    if (!canvas || !box) return undefined
    let phase = 0
    // The width the buffer was last sized for. `draw` runs on every animation frame of a battle
    // pulse and on every resize notification, and *sizing* the canvas is the only part of it that
    // can change the layout — writing `canvas.style.height` inside a box a ResizeObserver is
    // watching is what makes the loop the browser reports as "ResizeObserver loop completed with
    // undelivered notifications". Sized once per width, that feedback path is gone and the pulse
    // costs a paint rather than a reflow.
    let sizedFor = null
    // The active world (`utils/world`): the Game view keys this component by the map size, so one
    // mount draws one world.
    const { columns: COLS, rows: ROWS, matrix } = activeWorld()
    const draw = () => {
      const width = Math.max(1, Math.floor(box.clientWidth))
      const cell = width / COLS
      const dpr = window.devicePixelRatio || 1
      if (sizedFor !== `${width}x${dpr}`) {
        sizedFor = `${width}x${dpr}`
        // The *buffer* only. The box's `aspect-ratio` decides how tall the canvas is drawn and CSS
        // stretches it to fill; writing a pixel height here put the draw back inside the layout,
        // which is both how the ratio broke and what the ResizeObserver kept re-triggering on.
        canvas.width = Math.round(width * dpr)
        canvas.height = Math.round(cell * ROWS * dpr)
      }
      const ctx = canvas.getContext("2d")
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.fillStyle = SEA
      ctx.fillRect(0, 0, width, cell * ROWS)
      ctx.fillStyle = LAND
      for (const key of Object.keys(matrix)) {
        const [col, row] = key.split("-").map(Number)
        ctx.fillRect(col * cell, row * cell, Math.ceil(cell), Math.ceil(cell))
      }
      for (const mark of marks) {
        if (mark.battle) {
          // An alarm, not a dot: a hot core, expanding warning rings radar-style, and sparks
          // orbiting off it. Impossible to miss on a map this size.
          const pulse = 0.45 + 0.55 * Math.abs(Math.sin(phase))
          const cx = mark.col * cell + cell / 2
          const cy = mark.row * cell + cell / 2
          ctx.save()

          // Two expanding rings, half a cycle apart, fading as they grow.
          for (const offset of [0, 0.5]) {
            const progress = (phase / Math.PI + offset) % 1
            const ringRadius = 2 + progress * 13
            ctx.strokeStyle = `rgba(255, 90, 60, ${(1 - progress) * 0.9})`
            ctx.lineWidth = 1.6
            ctx.beginPath()
            ctx.arc(cx, cy, ringRadius, 0, Math.PI * 2)
            ctx.stroke()
          }

          // Sparks thrown off the blaze, orbiting slowly.
          for (let i = 0; i < 5; i++) {
            const angle = (i * Math.PI * 2) / 5 + phase * 0.6
            const distance = 5 + 2.5 * Math.sin(phase * 2 + i * 1.7)
            ctx.fillStyle = `rgba(255, ${200 + Math.round(40 * Math.sin(phase + i))}, 120, ${0.5 + 0.5 * Math.abs(Math.sin(phase + i))})`
            ctx.fillRect(cx + Math.cos(angle) * distance - 1, cy + Math.sin(angle) * distance - 1, 2, 2)
          }

          // The core last, glowing on top of everything.
          ctx.shadowColor = `rgba(255, 140, 40, ${pulse})`
          ctx.shadowBlur = 6 + 8 * pulse
          ctx.fillStyle = `rgba(255, ${Math.round(160 + 80 * pulse)}, 60, ${0.75 + 0.25 * pulse})`
          ctx.beginPath()
          ctx.arc(cx, cy, Math.max(2, cell * (0.55 + 0.3 * pulse)), 0, Math.PI * 2)
          ctx.fill()
          ctx.restore()
          continue
        }
        const colour = mark.paint
        if (!colour) continue
        ctx.fillStyle = rgba(colour, mark.alpha)
        if (mark.dot) {
          const pad = cell * 0.25
          ctx.fillRect(mark.col * cell + pad, mark.row * cell + pad, Math.max(1, cell - 2 * pad), Math.max(1, cell - 2 * pad))
        } else {
          ctx.fillRect(mark.col * cell, mark.row * cell, Math.ceil(cell), Math.ceil(cell))
        }
      }
    }
    draw()
    /* And the notification is answered on the next frame rather than inside the callback. Even
       guarded, a synchronous write from an observer's own callback is what the loop detector is
       looking for; deferring puts it in the following frame, where it is an ordinary paint. */
    let frame = null
    const onResize = () => {
      if (frame !== null) return
      // A timeout rather than an animation frame: this app is a game somebody leaves open in a
      // background tab, and a hidden tab runs no animation frames at all — the radar would come
      // back from a resize still drawn at its old width. A macrotask is late enough to be outside
      // the observation cycle, which is all the loop detector asks for.
      frame = setTimeout(() => {
        frame = null
        draw()
      }, 0)
    }
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(onResize) : null
    observer?.observe(box)
    window.addEventListener("resize", onResize)
    // Only battles animate; a quiet map costs nothing.
    const hasBattles = marks.some((mark) => mark.battle)
    const timer = hasBattles
      ? setInterval(() => {
          phase += 0.35
          draw()
        }, 120)
      : null
    return () => {
      observer?.disconnect()
      window.removeEventListener("resize", onResize)
      if (frame !== null) clearTimeout(frame)
      if (timer) clearInterval(timer)
    }
  }, [marks])

  // Click jumps; press-and-drag scrubs — the big map follows the pointer, like dragging the map
  // itself but at world scale. Listeners for move/up sit on the window so a drag that wanders off
  // the little box keeps working until the button is released.
  useEffect(() => {
    const box = miniMapRef.current
    if (!box) return undefined
    let dragging = false

    // A mouse event carries the point itself; a touch event carries a list of them. Everything
    // below works on whichever it was handed, so the scrub is written once.
    const pointOf = (e) => (e.touches ? e.touches[0] : e.changedTouches ? e.changedTouches[0] : e)

    const positionOf = (e) => {
      const point = pointOf(e)
      if (!point) return null
      const rect = box.getBoundingClientRect()
      const percentX = Math.max(Math.min(((point.clientX - rect.left) / rect.width) * 100, 100), 0)
      const percentY = Math.max(Math.min(((point.clientY - rect.top) / rect.height) * 100, 100), 0)
      return [percentX, percentY]
    }

    const handleDown = (e) => {
      const position = positionOf(e)
      if (!position) return
      dragging = true
      setClickOnMinimap(position)
      // On touch this is also what keeps the gesture from scrolling the dock underneath it.
      e.preventDefault()
    }
    const handleMove = (e) => {
      if (!dragging) return
      const position = positionOf(e)
      if (position) setClickOnMinimap(position)
    }
    const handleUp = () => {
      dragging = false
    }

    box.addEventListener("mousedown", handleDown)
    window.addEventListener("mousemove", handleMove)
    window.addEventListener("mouseup", handleUp)
    box.addEventListener("touchstart", handleDown, { passive: false })
    window.addEventListener("touchmove", handleMove, { passive: false })
    window.addEventListener("touchend", handleUp)
    window.addEventListener("touchcancel", handleUp)

    return () => {
      box.removeEventListener("mousedown", handleDown)
      window.removeEventListener("mousemove", handleMove)
      window.removeEventListener("mouseup", handleUp)
      box.removeEventListener("touchstart", handleDown)
      window.removeEventListener("touchmove", handleMove)
      window.removeEventListener("touchend", handleUp)
      window.removeEventListener("touchcancel", handleUp)
    }
  }, [setClickOnMinimap])

  // The viewport marker follows the big map, including the position it had before this mounted.
  useEffect(() => {
    const marker = radarMarkerRef.current
    if (!marker) return
    if (!radarPosition) {
      marker.style.display = "none"
      return
    }
    marker.style.display = ""
    marker.style.left = `${radarPosition[0]}%`
    marker.style.top = `${radarPosition[1]}%`
  }, [radarPosition])

  return (
    <div id="mini-map-button-wrapper">
      <div id="mini-map-container" ref={miniMapRef}>
        <canvas id="mini-map" ref={canvasRef} aria-label={t("game.miniMap.alt")} role="img" />
        <div className="radar-marker" ref={radarMarkerRef}></div>
        {battles.length > 0 && (
          <button
            type="button"
            className="minimap-alert"
            title={t("game.miniMap.battleAlert", { count: battles.length, context: pointerContext })}
            onClick={(e) => {
              // Jump the big map to the (first) fire rather than to where the click landed.
              e.stopPropagation()
              setClickOnMinimap([
                ((battles[0].col + 0.5) * 100) / activeWorld().columns,
                ((battles[0].row + 0.5) * 100) / activeWorld().rows,
              ])
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            🔥 {battles.length}
          </button>
        )}
      </div>
      {/* The grid eye moved onto the map itself (owner's ask, 2026-09-04) — see `MapControls` —
          and the zoom pair is gone (the wheel zooms). The column renders only if a caller still
          hands it the eye, rather than an empty 34px gutter beside the world. */}
      {onToggleGrid && (
        <div id="controls">
          {/* The show's speed (requirement 36, M10), on top of the grid's button as on the map. */}
          {speed && (
            <button
              className="speed-toggle"
              onClick={speed.onChange}
              title={t("game.miniMap.showSpeed", { speed: speed.value })}
              aria-label={t("game.miniMap.showSpeed", { speed: speed.value })}
            >
              x{speed.value}
            </button>
          )}
          <button
            className={`grid-toggle${gridVisible ? "" : " off"}`}
            onClick={onToggleGrid}
            aria-pressed={!gridVisible}
            title={gridVisible ? t("game.miniMap.hideGrid") : t("game.miniMap.showGrid")}
            aria-label={gridVisible ? t("game.miniMap.hideGrid") : t("game.miniMap.showGrid")}
          >
            <GridIcon size={20} off={!gridVisible} />
          </button>
        </div>
      )}
    </div>
  )
}

export default MiniMap
